import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import {
  PhUserCircle,
  PhUsersThree,
  PhClipboardText,
  PhBooks,
  PhMoney,
  PhCoins,
  PhExam,
  PhCheck,
} from 'ember-phosphor-icons';
import SectionPanel from 'tio-ui/components/section-panel';
import ReadoutSummaryPanel from './summary-panel';
import ReadoutFieldTable from './field-table';
import ReadoutInstructionsPanel from './instructions-panel';
import ReadoutDocumentsPanel from './documents-panel';
import ReadoutApprovalsPanel from './approvals-panel';
import {
  SECTIONS,
  getFieldValue,
  getFieldValues,
} from '../../../utils/tas/program-builder/sections';
import { buildSummary } from '../../../utils/tas/program-builder/summary';
import {
  EMPLOYEE_FIELDS,
  DEPENDENT_FIELDS,
  PROGRAM_INFO_FIELDS,
  COURSE_DETAIL_FIELDS,
  COURSE_EXPENSE_FIELDS,
  SCHOLARSHIP_FIELDS,
  CREDIT_REQUIRED_COURSE_FIELDS,
  ACADEMIC_GRADES,
} from '../../../utils/tas/program-builder/field-groups';
import type { SectionId, ProgramTypeMeta } from '../../../utils/tas/program-builder/sections';
import type { SummaryGroup } from '../../../utils/tas/program-builder/summary';
import type { FieldDescriptor } from '../../../utils/tas/program-builder/field-groups';
import type { CustomFieldSignature } from 'tio-data/types/tuition-assistance';
import type IntlService from 'ember-intl/services/intl';
import type { TasProgramTemplateExtension as TASProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';

type FieldsSignature = TASProgramTemplateModel['fields'];

const NO_FORCED: string[] = [];

interface ReadoutSectionContentSignature {
  Args: {
    model: TASProgramTemplateModel;
    /** Field-config hash to read from — the model's `fields`, or the admin
     * builder's working draft (identical to saved state while read-only). */
    fields: FieldsSignature;
    typeMeta: ProgramTypeMeta;
    activeSectionId: SectionId;
    /** Reveal internal FIELD_NAMES badges (the admin builder's devops toggle). */
    showFieldNames?: boolean;
  };
  Element: HTMLDivElement;
}

/**
 * The static body of one readout section — the same panels for every consumer:
 * the employee readout wraps it in its passive-rail shell, and the tio-admin
 * builder renders it as the center column for launched (read-only) programs.
 * No inputs or edit affordances; `@showFieldNames` is display-only.
 */
export default class ReadoutSectionContent extends Component<ReadoutSectionContentSignature> {
  @service declare intl: IntlService;

  employeeIcon = PhUserCircle;
  dependentIcon = PhUsersThree;
  programIcon = PhClipboardText;
  courseIcon = PhBooks;
  expenseIcon = PhMoney;
  scholarshipIcon = PhCoins;
  gradesIcon = PhExam;
  courseDetailFields = COURSE_DETAIL_FIELDS;
  courseExpenseFields = COURSE_EXPENSE_FIELDS;
  scholarshipFields = SCHOLARSHIP_FIELDS;

  get fields(): FieldsSignature {
    return this.args.fields;
  }

  get summaryGroups(): SummaryGroup[] {
    return buildSummary({
      fields: this.fields,
      meta: this.args.typeMeta,
      code: this.args.model.code ?? '',
      isTrial: this.args.model.isTrial ?? false,
      t: (key, args) => this.intl.t(key, args),
      locale: this.intl.primaryLocale,
    });
  }

  /**
   * Summary groups for the active section. Groups whose home section is hidden
   * for this program type (e.g. the tracking-only case, where "Program options"
   * points at the hidden Limits section) fold into Basics so nothing configured
   * ever disappears from the read-out.
   */
  get groupsForActiveSection(): SummaryGroup[] {
    const hidden = new Set(
      SECTIONS.filter((section) => section.applies(this.args.typeMeta) === 'na').map(
        (section) => section.id
      )
    );
    return this.summaryGroups.filter(
      (group) =>
        group.sectionId === this.args.activeSectionId ||
        (this.args.activeSectionId === 'basics' && hidden.has(group.sectionId))
    );
  }

  // ── application / courses field tables ─────────────────────────────────────

  /** Tracking-only (Type 1) programs expose only PROGRAM/EMPLOYEE-province
   * built-in fields — same filter as the builder's Application section. */
  private forType(list: FieldDescriptor[]): FieldDescriptor[] {
    if (!this.args.typeMeta.trackingOnly) {
      return list;
    }
    return list.filter((d) => d.province === 'PROGRAM' || d.province === 'EMPLOYEE');
  }

  get employeeFields(): FieldDescriptor[] {
    return this.forType(EMPLOYEE_FIELDS);
  }
  get dependentFields(): FieldDescriptor[] {
    return this.forType(DEPENDENT_FIELDS);
  }
  get programInfoFields(): FieldDescriptor[] {
    return this.forType(PROGRAM_INFO_FIELDS);
  }

  get showDependentFields(): boolean {
    return (
      Boolean(getFieldValue(this.fields, 'DEPENDENT_PROGRAM')) && this.dependentFields.length > 0
    );
  }

  private customsFor(province: CustomFieldSignature['province']): CustomFieldSignature[] {
    return (this.args.model.customFields ?? []).filter((field) => field.province === province);
  }

  get customEmployeeFields() {
    return this.customsFor('EMPLOYEE');
  }
  get customProgramFields() {
    return this.customsFor('PROGRAM');
  }
  get customApplicationFields() {
    return this.customsFor('APPLICATION');
  }
  get customCourseFields() {
    return this.customsFor('COURSE');
  }

  get creditMode(): boolean {
    return getFieldValue(this.fields, 'LIMIT_CAPACITY') === 'CREDITS';
  }

  get forcedCourseFields(): string[] {
    return this.creditMode ? CREDIT_REQUIRED_COURSE_FIELDS : NO_FORCED;
  }

  /** Every academic grade with its passing/exclusive state, mirroring the
   * builder's grade pills so admins can see the full scale at a glance. */
  private gradeRows(field: string) {
    const selected = getFieldValues(this.fields, field) as string[];
    return ACADEMIC_GRADES.map((grade) => ({ grade, on: selected.includes(grade) }));
  }

  get passingGradeRows() {
    return this.gradeRows('PASSING_ACADEMIC_PERFORMANCES');
  }

  get exclusiveGradeRows() {
    return this.gradeRows('EXCLUSIVE_ACADEMIC_PERFORMANCE_OPTIONS');
  }

  get expenseType(): string {
    return String(getFieldValue(this.fields, 'EXPENSE_TYPE') ?? '').trim();
  }

  get fieldOptions() {
    return this.args.model.tasFieldOptions ?? [];
  }

  // ── instructions & faqs ─────────────────────────────────────────────────────

  get instructions() {
    return this.args.model.programApproverInstructions;
  }

  get faqs() {
    return this.args.model.programFaqs;
  }

  <template>
    {{#if (eq @activeSectionId "application")}}
      <div class="space-y-5" ...attributes>
        <ReadoutFieldTable
          @title={{t "program_builder.application.employee_information"}}
          @icon={{this.employeeIcon}}
          @descriptors={{this.employeeFields}}
          @fields={{this.fields}}
          @fieldOptions={{this.fieldOptions}}
          @showFieldNames={{@showFieldNames}}
        />
        {{#if this.showDependentFields}}
          <ReadoutFieldTable
            @title={{t "program_builder.application.dependent_information"}}
            @icon={{this.dependentIcon}}
            @descriptors={{this.dependentFields}}
            @fields={{this.fields}}
            @fieldOptions={{this.fieldOptions}}
            @showFieldNames={{@showFieldNames}}
          />
        {{/if}}
        {{#if this.customEmployeeFields.length}}
          <ReadoutFieldTable
            @title={{t "program_builder.application.custom_employee_fields"}}
            @icon={{this.employeeIcon}}
            @customFields={{this.customEmployeeFields}}
            @fields={{this.fields}}
            @fieldOptions={{this.fieldOptions}}
            @showFieldNames={{@showFieldNames}}
          />
        {{/if}}
        <ReadoutFieldTable
          @title={{t "program_builder.application.program_school_information"}}
          @icon={{this.programIcon}}
          @descriptors={{this.programInfoFields}}
          @fields={{this.fields}}
          @fieldOptions={{this.fieldOptions}}
          @showFieldNames={{@showFieldNames}}
        />
        {{#if this.customProgramFields.length}}
          <ReadoutFieldTable
            @title={{t "program_builder.application.custom_program_fields"}}
            @icon={{this.programIcon}}
            @customFields={{this.customProgramFields}}
            @fields={{this.fields}}
            @fieldOptions={{this.fieldOptions}}
            @showFieldNames={{@showFieldNames}}
          />
        {{/if}}
      </div>
    {{else if (eq @activeSectionId "courses")}}
      <div class="space-y-5" ...attributes>
        <SectionPanel
          @icon={{this.gradesIcon}}
          @title={{t "program_builder.courses.grades_expenses"}}
          @iconColor="violet"
        >
          <:body>
            <div class="mb-1.5 tio-text-sm-medium tio-fg-secondary">
              {{t "program_builder.courses.passing_grades"}}
              {{#if @showFieldNames}}
                <code
                  class="ml-1 font-mono tio-text-micro tio-fg-brand"
                >PASSING_ACADEMIC_PERFORMANCES</code>
              {{/if}}
            </div>
            <div class="tio-wrap-tight" data-test-readout-passing-grades>
              {{#each this.passingGradeRows as |row|}}
                <span
                  class="tio-row-extra-tight rounded-full border px-3 py-1 text-sm
                    {{if
                      row.on
                      'tio-border-success tio-bg-success-soft tio-fg-success'
                      'tio-border-default tio-fg-secondary'
                    }}"
                >
                  {{#if row.on}}
                    <PhCheck @size={{12}} aria-hidden="true" />
                  {{/if}}
                  {{row.grade}}
                </span>
              {{/each}}
            </div>

            <div class="mb-1.5 mt-4 tio-text-sm-medium tio-fg-secondary">
              {{t "program_builder.courses.exclusive_grades"}}
              <span class="font-normal tio-fg-muted">{{t
                  "program_builder.courses.exclusive_grades_hint"
                }}</span>
              {{#if @showFieldNames}}
                <code
                  class="ml-1 font-mono tio-text-micro tio-fg-brand"
                >EXCLUSIVE_ACADEMIC_PERFORMANCE_OPTIONS</code>
              {{/if}}
            </div>
            <div class="tio-wrap-tight" data-test-readout-exclusive-grades>
              {{#each this.exclusiveGradeRows as |row|}}
                <span
                  class="tio-row-extra-tight rounded-full border px-3 py-1 text-sm
                    {{if
                      row.on
                      'tio-border-brand tio-bg-brand-soft tio-fg-brand'
                      'tio-border-default tio-fg-secondary'
                    }}"
                >
                  {{#if row.on}}
                    <PhCheck @size={{12}} aria-hidden="true" />
                  {{/if}}
                  {{row.grade}}
                </span>
              {{/each}}
            </div>

            <div class="mt-4">
              <div class="tio-row-between gap-3 text-sm">
                <span class="tio-fg-muted">
                  {{t "program_builder.courses.expense_type_label"}}
                  {{#if @showFieldNames}}
                    <code class="ml-1 font-mono tio-text-micro tio-fg-brand">EXPENSE_TYPE</code>
                  {{/if}}
                </span>
                {{! Blank means the app falls back to "Courses" — show the
                effective default rather than an unexplained em dash. }}
                <span class="font-medium">
                  {{if
                    this.expenseType
                    this.expenseType
                    (t "program_builder.courses.expense_type_placeholder")
                  }}
                </span>
              </div>
              <p class="mb-0 mt-1 text-xs tio-fg-muted">
                {{t "program_builder.courses.expense_type_hint"}}
              </p>
            </div>
          </:body>
        </SectionPanel>
        <ReadoutFieldTable
          @title={{t "program_builder.courses.course_details"}}
          @icon={{this.courseIcon}}
          @descriptors={{this.courseDetailFields}}
          @fields={{this.fields}}
          @forcedRequired={{this.forcedCourseFields}}
          @fieldOptions={{this.fieldOptions}}
          @showFieldNames={{@showFieldNames}}
        />
        <ReadoutFieldTable
          @title={{t "program_builder.courses.expenses"}}
          @icon={{this.expenseIcon}}
          @descriptors={{this.courseExpenseFields}}
          @fields={{this.fields}}
          @forcedRequired={{this.forcedCourseFields}}
          @fieldOptions={{this.fieldOptions}}
          @showFieldNames={{@showFieldNames}}
        />
        <ReadoutFieldTable
          @title={{t "program_builder.courses.scholarship_fields"}}
          @icon={{this.scholarshipIcon}}
          @descriptors={{this.scholarshipFields}}
          @fields={{this.fields}}
          @fieldOptions={{this.fieldOptions}}
          @showFieldNames={{@showFieldNames}}
        />
        {{#if this.customApplicationFields.length}}
          <ReadoutFieldTable
            @title={{t "program_builder.courses.custom_application_fields"}}
            @icon={{this.courseIcon}}
            @customFields={{this.customApplicationFields}}
            @fields={{this.fields}}
            @fieldOptions={{this.fieldOptions}}
            @showFieldNames={{@showFieldNames}}
          />
        {{/if}}
        {{#if this.customCourseFields.length}}
          <ReadoutFieldTable
            @title={{t "program_builder.courses.custom_course_fields"}}
            @icon={{this.courseIcon}}
            @customFields={{this.customCourseFields}}
            @fields={{this.fields}}
            @fieldOptions={{this.fieldOptions}}
            @showFieldNames={{@showFieldNames}}
          />
        {{/if}}
      </div>
    {{else if (eq @activeSectionId "documents")}}
      <ReadoutDocumentsPanel
        @fields={{this.fields}}
        @typeMeta={{@typeMeta}}
        @showFieldNames={{@showFieldNames}}
        ...attributes
      />
    {{else if (eq @activeSectionId "approvals")}}
      <ReadoutApprovalsPanel
        @fields={{this.fields}}
        @typeMeta={{@typeMeta}}
        @model={{@model}}
        @showFieldNames={{@showFieldNames}}
        ...attributes
      />
    {{else if (eq @activeSectionId "instructions")}}
      <ReadoutInstructionsPanel
        @instructions={{this.instructions}}
        @faqs={{this.faqs}}
        ...attributes
      />
    {{else if this.groupsForActiveSection.length}}
      <ReadoutSummaryPanel @groups={{this.groupsForActiveSection}} ...attributes />
    {{else}}
      <p class="text-sm tio-fg-muted" ...attributes>
        {{t "program_builder.readout.nothing_configured"}}
      </p>
    {{/if}}
  </template>
}
